import React from 'react'
import {
  defaultColumnHeaders,
  defaultRowHeaders,
  getColumnTypes,
  getTableDimensions,
  parseTableCells,
  resizeHeaders,
  type TableColumnType
} from './tableFieldUtils'
import {
  getTableRowConditionalFieldCount,
  getTableRowPrefillSettings,
  isTableRowConditionalConfigured,
  tableHasRowConditionalEdit
} from './prefillFieldSettingsUtils'
import './TableField.scss'

interface Props {
  element: any
  interactive?: boolean
  isPrefillForm?: boolean
  selectedColumnIndex?: number | null
  onColumnSelect?: (columnIndex: number) => void
  selectedRowIndex?: number | null
  onRowSelect?: (rowIndex: number) => void
  onCellChange?: (rowIndex: number, colIndex: number, value: string) => void
}

const renderCellInput = (
  columnType: TableColumnType,
  cell: string,
  interactive: boolean,
  onChange: (value: string) => void
) => {
  if (!interactive) {
    if (columnType === 'file' && cell) {
      return (
        <a href={cell} target='_blank' rel='noreferrer' className='small'>
          View file
        </a>
      )
    }
    return <span className='text-muted'>{cell || '—'}</span>
  }

  if (columnType === 'file') {
    return (
      <div>
        <input
          type='file'
          className='form-control form-control-sm'
          onChange={(event) => {
            const file = event.target.files?.[0]
            onChange(file ? file.name : '')
          }}
        />
        {cell ? <small className='text-muted d-block mt-1'>{cell}</small> : null}
      </div>
    )
  }

  return (
    <input
      type={columnType === 'number' ? 'number' : 'text'}
      className='form-control form-control-sm'
      value={cell}
      placeholder='Enter value'
      onChange={(event) => onChange(event.target.value)}
    />
  )
}

const TableFieldPreview: React.FC<Props> = ({
  element,
  interactive = false,
  isPrefillForm = false,
  selectedColumnIndex = null,
  onColumnSelect,
  selectedRowIndex = null,
  onRowSelect,
  onCellChange
}) => {
  const { rows, cols } = getTableDimensions(element)
  const columnHeaders = resizeHeaders(element.columnHeaders, cols, defaultColumnHeaders)
  const rowHeaders = resizeHeaders(element.rowHeaders, rows, defaultRowHeaders)
  const columnTypes = getColumnTypes(element)
  const cells = parseTableCells(element.value, rows, cols)
  const showRowLabelColumn = element.showRowHeaders === true || isPrefillForm
  const showConditionalBadgeColumn = isPrefillForm && tableHasRowConditionalEdit(element)
  const clickableColumns = isPrefillForm && typeof onColumnSelect === 'function'
  const clickableRows = isPrefillForm && typeof onRowSelect === 'function'

  return (
    <div className='table-responsive table-field-wrap'>
      <table className='table-field align-middle mb-0'>
        <thead>
          <tr>
            {showConditionalBadgeColumn ? (
              <th className='table-conditional-marker-col' aria-label='Conditional edit' />
            ) : null}
            {showRowLabelColumn ? <th style={{ width: 120 }} /> : null}
            {columnHeaders.map((header, colIndex) => {
              const isSelected = selectedColumnIndex === colIndex
              return (
                <th
                  key={`col-${colIndex}`}
                  style={{ color: element.color }}
                  className={isSelected ? 'table-column-selected' : undefined}
                >
                  {clickableColumns ? (
                    <button
                      type='button'
                      className={`btn btn-link p-0 text-decoration-none fw-semibold ${
                        isSelected ? 'text-warning' : ''
                      }`}
                      style={{ color: isSelected ? undefined : element.color }}
                      onClick={(event) => {
                        event.stopPropagation()
                        onColumnSelect?.(colIndex)
                      }}
                    >
                      {header}
                    </button>
                  ) : (
                    <div>{header}</div>
                  )}
                  <small className='text-muted fw-normal d-block'>{columnTypes[colIndex]}</small>
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody>
          {cells.map((row, rowIndex) => {
            const rowConfig = getTableRowPrefillSettings(element, rowIndex)
            const hasConditional = isTableRowConditionalConfigured(element, rowIndex)
            const conditionalFieldCount = getTableRowConditionalFieldCount(element, rowIndex)
            const isSelected = selectedRowIndex === rowIndex
            const rowClassName = [
              hasConditional ? 'table-row-conditional' : '',
              isSelected ? 'table-row-selected-row' : ''
            ]
              .filter(Boolean)
              .join(' ')

            return (
              <tr key={`row-${rowIndex}`} className={rowClassName || undefined}>
                {showConditionalBadgeColumn ? (
                  <td className='table-conditional-marker-col text-center align-middle'>
                    {hasConditional ? (
                      <span
                        className='badge bg-warning text-dark table-conditional-badge'
                        title={`${conditionalFieldCount} conditional edit field${
                          conditionalFieldCount === 1 ? '' : 's'
                        }`}
                      >
                        {conditionalFieldCount}
                      </span>
                    ) : null}
                  </td>
                ) : null}
                {showRowLabelColumn ? (
                  <th
                    scope='row'
                    className={`text-muted fw-medium ${isSelected ? 'table-row-selected' : ''}`}
                  >
                    {clickableRows ? (
                      <button
                        type='button'
                        className={`btn btn-link p-0 text-decoration-none ${
                          isSelected ? 'text-warning fw-semibold' : 'text-muted'
                        }`}
                        onClick={(event) => {
                          event.stopPropagation()
                          onRowSelect?.(rowIndex)
                        }}
                      >
                        {rowHeaders[rowIndex]}
                      </button>
                    ) : (
                      rowHeaders[rowIndex]
                    )}
                    {rowConfig.allowEdit && !hasConditional ? (
                      <small className='d-block text-primary'>Editable</small>
                    ) : null}
                  </th>
                ) : null}
                {row.map((cell, colIndex) => (
                  <td key={`cell-${rowIndex}-${colIndex}`}>
                    {renderCellInput(columnTypes[colIndex], cell, interactive, (value) =>
                      onCellChange?.(rowIndex, colIndex, value)
                    )}
                  </td>
                ))}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

export default TableFieldPreview
